import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { typeBadge } from '../hygiene/types';
import { statusStyle } from '../../utils/reportFormat';
import Avatar from './Avatar';
import { EpicSummaryItem, GROUP_STYLE, StatusGroup } from './types';
import * as fmt from '../../lib/format';

const JIRA_BROWSE = 'https://saral-tracker.atlassian.net/browse/';
const PAGE_SIZE = 25;

interface Props {
  items: EpicSummaryItem[];
  filter: StatusGroup | null;
  onClearFilter: () => void;
}

function KeyChip({ issueKey }: { issueKey: string }) {
  return (
    <a
      href={`${JIRA_BROWSE}${issueKey}`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-1.5 py-0.5 text-[0.6875rem] font-semibold text-blue-700 ring-1 ring-inset ring-blue-200/60 transition-colors hover:bg-blue-100"
    >
      {issueKey}
      <ExternalLink className="h-3 w-3 opacity-60" aria-hidden />
    </a>
  );
}

function StatusPill({ status, small }: { status: string; small?: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ring-1 ring-inset ${statusStyle(status).badge} ${
        small ? 'px-1.5 py-0.5 text-[0.6875rem]' : 'px-2 py-0.5 text-xs'
      }`}
    >
      {status}
    </span>
  );
}

function SubtaskRow({ item }: { item: EpicSummaryItem }) {
  return (
    <li className="flex items-start gap-3 py-2.5">
      <div className="min-w-0 flex-1">
        <KeyChip issueKey={item.issue_key} />
        <p className="mt-1 line-clamp-2 text-[0.8125rem] leading-snug text-gray-700" title={item.name}>
          {item.name || 'Untitled'}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <StatusPill status={item.status} small />
        <div className="flex items-center gap-2 text-xs tabular-nums text-gray-500">
          {item.hours_period > 0 && <span>{fmt.hours(item.hours_period)}</span>}
          {item.assignee && <Avatar name={item.assignee.name} size="sm" />}
        </div>
      </div>
    </li>
  );
}

function ItemRow({ item, subtasks }: { item: EpicSummaryItem; subtasks: EpicSummaryItem[] }) {
  const [open, setOpen] = useState(false);
  const subDone = subtasks.filter((s) => s.group === 'done').length;
  return (
    <li className="px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <KeyChip issueKey={item.issue_key} />
            <span className={`rounded-full px-2 py-0.5 text-[0.6875rem] font-medium ring-1 ring-inset ${typeBadge(item.issue_type)}`}>
              {item.issue_type}
            </span>
          </div>
          <p className="mt-1 line-clamp-2 text-sm leading-snug text-gray-900" title={item.name}>
            {item.name || 'Untitled'}
          </p>
          {subtasks.length > 0 && (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              className="mt-1.5 inline-flex items-center gap-1 rounded-md py-1 pr-2 text-xs font-medium text-gray-500 transition-[color,transform] duration-150 hover:text-gray-800 active:scale-[0.98] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <ChevronRight
                className={`h-3.5 w-3.5 transition-transform duration-200 ease-out motion-reduce:transition-none ${open ? 'rotate-90' : ''}`}
                aria-hidden
              />
              {subtasks.length} {subtasks.length === 1 ? 'sub-task' : 'sub-tasks'} · {subDone} done
            </button>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <StatusPill status={item.status} />
          <div className="flex items-center gap-2 text-xs tabular-nums text-gray-500">
            {item.hours_period > 0 && <span>{fmt.hours(item.hours_period)}</span>}
            {item.assignee && <Avatar name={item.assignee.name} size="sm" />}
          </div>
        </div>
      </div>
      {open && subtasks.length > 0 && (
        <ul className="ml-1 mt-2 divide-y divide-gray-100 border-l-2 border-gray-100 pl-3">
          {subtasks.map((s) => (
            <SubtaskRow key={s.issue_key} item={s} />
          ))}
        </ul>
      )}
    </li>
  );
}

// Everything inside the epic. Tickets come first with their sub-tasks tucked underneath;
// a sub-task whose ticket is not in the epic is listed on its own.
export default function ItemList({ items, filter, onClearFilter }: Props) {
  const [shown, setShown] = useState(PAGE_SIZE);
  useEffect(() => setShown(PAGE_SIZE), [filter]);

  const { rows, subsByParent } = useMemo(() => {
    const tickets = items.filter((i) => !i.is_subtask);
    const keys = new Set(tickets.map((t) => t.issue_key));
    const subs = new Map<string, EpicSummaryItem[]>();
    const orphans: EpicSummaryItem[] = [];
    for (const i of items) {
      if (!i.is_subtask) continue;
      if (i.parent_key && keys.has(i.parent_key)) {
        const list = subs.get(i.parent_key) ?? [];
        list.push(i);
        subs.set(i.parent_key, list);
      } else {
        orphans.push(i);
      }
    }
    const keep = (i: EpicSummaryItem) => !filter || i.group === filter;
    return { rows: [...tickets.filter(keep), ...orphans.filter(keep)], subsByParent: subs };
  }, [items, filter]);

  const visible = rows.slice(0, shown);
  const remaining = rows.length - visible.length;

  return (
    <div>
      {filter && (
        <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50/70 px-4 py-2.5 text-xs text-gray-500">
          <span>
            Showing {rows.length} {GROUP_STYLE[filter].label.toLowerCase()} {rows.length === 1 ? 'ticket' : 'tickets'}
          </span>
          <button
            type="button"
            onClick={onClearFilter}
            className="rounded-md px-2 py-1 font-medium text-blue-700 transition-[color,transform] hover:text-blue-900 active:scale-[0.97] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Show all
          </button>
        </div>
      )}
      {rows.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-gray-500">
          {items.length === 0 ? 'Nothing is linked to this epic yet.' : 'No tickets with this status.'}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {visible.map((i) => (
            <ItemRow key={i.issue_key} item={i} subtasks={subsByParent.get(i.issue_key) ?? []} />
          ))}
        </ul>
      )}
      {remaining > 0 && (
        <div className="border-t border-gray-100 p-2">
          <button
            type="button"
            onClick={() => setShown((n) => n + PAGE_SIZE)}
            className="w-full rounded-xl px-3 py-2 text-sm font-medium text-blue-700 transition-[background-color,transform] duration-150 hover:bg-blue-50/70 active:scale-[0.99] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Show {Math.min(PAGE_SIZE, remaining)} more <span className="text-gray-500">· {remaining} left</span>
          </button>
        </div>
      )}
    </div>
  );
}
